import { getCarouselUrls, isCarouselItem } from "./carouselUtils";
import {
  MEDIA_DIMENSIONS,
  MAX_CAROUSEL_IMAGES,
  MIN_CAROUSEL_IMAGES,
  VIDEO_FORMATS,
} from "./constants";
import { getDimensionCategory } from "./dimensions";
import { getExtensionFile, isLikelyImageUrl } from "./string";

/** Max static images the user can select for batch edit. */
export const BATCH_EDIT_MAX_SELECTION = 15;

/** Source images (carousel slides count) that fit the 20 min resize budget. */
export const BATCH_RESIZE_MAX_SOURCE_IMAGES = 30;

/** Items per API request (must match backend `BATCH_EDIT_MAX_ITEMS`). */
export const BATCH_EDIT_CHUNK_SIZE = 5;

/** @deprecated Prefer {@link BATCH_EDIT_CHUNK_SIZE} or {@link BATCH_EDIT_MAX_SELECTION}. */
export const BATCH_EDIT_MAX_ITEMS = BATCH_EDIT_CHUNK_SIZE;

export const chunkGallerySelection = (
  items,
  chunkSize = BATCH_EDIT_CHUNK_SIZE
) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }

  return chunks;
};

/**
 * Stable identifier for gallery multi-select. Prefer persisted content IDs;
 * fall back to stableKey/url so placeholders and generated items stay addressable.
 */
export const getGalleryItemSelectionId = (item) => {
  if (!item) return null;

  if (item.id != null && item.id !== "") {
    return String(item.id);
  }

  if (item.stableKey) {
    return String(item.stableKey);
  }

  if (item.url) {
    return String(item.url);
  }

  if (item.creationTime != null) {
    return `creation-${item.creationTime}`;
  }

  return null;
};

export const findGalleryItemBySelectionId = (items, selectionId) => {
  if (!selectionId || !Array.isArray(items)) {
    return null;
  }

  return (
    items.find((item) => getGalleryItemSelectionId(item) === selectionId) ||
    null
  );
};

export const getGalleryItemsBySelectionIds = (items, selectionIds) => {
  if (!Array.isArray(selectionIds) || selectionIds.length === 0) {
    return [];
  }

  return selectionIds
    .map((selectionId) => findGalleryItemBySelectionId(items, selectionId))
    .filter(Boolean);
};

const isStaticGalleryImage = (item) => {
  if (!item) return false;
  if (item.isGenerating) return false;
  if (item.resizingFormat) return false;
  if (isCarouselItem(item)) return false;

  const url = item.url;
  if (!url) return false;
  if (url === item.id) return false;
  if (url.startsWith("data:video/")) return false;

  const extension = getExtensionFile(url);
  if (VIDEO_FORMATS.includes(extension)) return false;

  return isLikelyImageUrl(url);
};

/**
 * Filters selection IDs to static images only (no carousel, video, or placeholders).
 * Caps at {@link BATCH_EDIT_MAX_SELECTION}.
 */
export const filterStaticImageSelectionIds = (items, selectionIds) => {
  if (!Array.isArray(selectionIds) || selectionIds.length === 0) {
    return [];
  }

  return selectionIds
    .filter((selectionId) => {
      const item = findGalleryItemBySelectionId(items, selectionId);
      return isStaticGalleryImage(item);
    })
    .slice(0, BATCH_EDIT_MAX_SELECTION);
};

export const getBatchEditEligibleItems = (items, selectionIds) => {
  const eligibleIds = filterStaticImageSelectionIds(items, selectionIds);

  return eligibleIds
    .map((selectionId) => {
      const item = findGalleryItemBySelectionId(items, selectionId);
      if (!item) return null;
      const index = items.findIndex(
        (entry) => getGalleryItemSelectionId(entry) === selectionId
      );

      return {
        selectionId,
        url: item.url,
        sourceItem: item,
        index: index >= 0 ? index : null,
      };
    })
    .filter(Boolean);
};

/**
 * Returns selected static images in selection order for creating a gallery carousel.
 * The whole selection must be valid static images (no videos, placeholders, or existing carousels)
 * and contain between {@link MIN_CAROUSEL_IMAGES} and {@link MAX_CAROUSEL_IMAGES} items.
 */
export const getCarouselCreationEligibleItems = (items, selectionIds) => {
  if (
    !Array.isArray(selectionIds) ||
    selectionIds.length < MIN_CAROUSEL_IMAGES ||
    selectionIds.length > MAX_CAROUSEL_IMAGES
  ) {
    return [];
  }

  const eligible = [];
  for (const selectionId of selectionIds) {
    const item = findGalleryItemBySelectionId(items, selectionId);
    if (!isStaticGalleryImage(item)) {
      return [];
    }

    eligible.push({
      selectionId,
      url: item.url,
      sourceItem: item,
    });
  }

  return eligible;
};

const isBatchResizeEligibleItem = (item) => {
  if (!item) return false;
  if (item.isGenerating) return false;
  if (item.resizingFormat) return false;
  if (isCarouselItem(item)) return true;
  return isStaticGalleryImage(item);
};

export const getBatchResizeEligibleItems = (items, selectionIds) => {
  if (!Array.isArray(selectionIds) || selectionIds.length === 0) {
    return [];
  }

  const mapped = selectionIds
    .map((selectionId) => {
      const item = findGalleryItemBySelectionId(items, selectionId);
      if (!isBatchResizeEligibleItem(item)) return null;
      const index = items.findIndex(
        (entry) => getGalleryItemSelectionId(entry) === selectionId
      );

      if (isCarouselItem(item)) {
        return {
          selectionId,
          type: "carousel",
          urls: getCarouselUrls(item),
          sourceItem: item,
          index: index >= 0 ? index : null,
        };
      }

      return {
        selectionId,
        type: "image",
        url: item.url,
        sourceItem: item,
        index: index >= 0 ? index : null,
      };
    })
    .filter(Boolean);

  const eligible = [];
  let sourceCount = 0;
  mapped.forEach((item) => {
    const itemSources =
      item.type === "carousel" ? Math.max((item.urls || []).length, 1) : 1;
    if (itemSources > BATCH_RESIZE_MAX_SOURCE_IMAGES) return;
    if (sourceCount + itemSources > BATCH_RESIZE_MAX_SOURCE_IMAGES) return;
    eligible.push(item);
    sourceCount += itemSources;
  });

  return eligible;
};

const countNeededFormatsForSource = ({ width, height, formats }) => {
  const requested = Array.isArray(formats) ? formats : [];
  if (!width || !height) return requested.length;

  const current = getDimensionCategory(width / height);
  return requested.filter(
    (format) => format === MEDIA_DIMENSIONS.custom || format !== current
  ).length;
};

const probeImageDimensions = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve({ width: null, height: null });
      return;
    }

    const img = new Image();
    let settled = false;
    const finish = (width, height) => {
      if (settled) return;
      settled = true;
      resolve({ width: width || null, height: height || null });
    };

    const timeoutId = setTimeout(() => finish(null, null), 8000);
    img.onload = () => {
      clearTimeout(timeoutId);
      finish(img.naturalWidth, img.naturalHeight);
    };
    img.onerror = () => {
      clearTimeout(timeoutId);
      finish(null, null);
    };
    img.src = url;
  });

const resolveSourceDimensions = async ({ width, height, url }) => {
  if (width && height) {
    return { width, height, probed: false };
  }
  const probed = await probeImageDimensions(url);
  return { ...probed, probed: true };
};

export const countBatchResizeSourceImages = (items = []) =>
  items.reduce((total, item) => {
    const sourceItem = item.sourceItem || item;
    if (item.type === "carousel" || isCarouselItem(sourceItem)) {
      const urls = item.urls || getCarouselUrls(sourceItem);
      return total + Math.max(urls.length, 1);
    }
    return total + 1;
  }, 0);

export const estimateBatchResizeCreditCount = async ({
  items = [],
  formats,
}) => {
  const requested = Array.isArray(formats) ? formats : [];
  let total = 0;

  for (const item of items) {
    const sourceItem = item.sourceItem || item;
    if (item.type === "carousel" || isCarouselItem(sourceItem)) {
      const slides = (
        Array.isArray(sourceItem.carouselImages) &&
        sourceItem.carouselImages.length > 0
          ? sourceItem.carouselImages
          : (item.urls || getCarouselUrls(sourceItem)).map((url) => ({ url }))
      ).filter(Boolean);

      for (const slide of slides) {
        const dims = await resolveSourceDimensions({
          width: slide.width,
          height: slide.height,
          url: slide.url,
        });
        total += countNeededFormatsForSource({
          width: dims.width,
          height: dims.height,
          formats: requested,
        });
      }
      continue;
    }

    const dims = await resolveSourceDimensions({
      width: sourceItem.width,
      height: sourceItem.height,
      url: item.url || sourceItem.url,
    });
    total += countNeededFormatsForSource({
      width: dims.width,
      height: dims.height,
      formats: requested,
    });
  }

  return Math.max(total, 1);
};
